"use client";

import { useIsClient } from "@shared/hooks/use-is-client";
import { useTheme } from "@shared/hooks/use-theme";
import { ToggleGroup, ToggleGroupItem } from "@ui/components/toggle-group";
import { cn } from "@ui/lib";
import { MonitorIcon, MoonIcon, SunIcon } from "lucide-react";

const OPTIONS = [
	{ value: "light", label: "Light theme", icon: SunIcon },
	{ value: "dark", label: "Dark theme", icon: MoonIcon },
	{ value: "system", label: "Match system theme", icon: MonitorIcon },
] as const;

type ThemeValue = (typeof OPTIONS)[number]["value"];

/** Three-way theme switch. Renders a same-size placeholder until hydrated. */
export function ColorModeToggle({ className }: { className?: string }) {
	const { theme, setTheme } = useTheme();
	const isClient = useIsClient();

	if (!isClient) {
		return (
			<span
				aria-hidden
				className={cn("block h-7 w-[5.25rem]", className)}
			/>
		);
	}

	return (
		<ToggleGroup
			value={[theme ?? "system"]}
			onValueChange={(value) => {
				const next = value[0] as ThemeValue | undefined;
				if (next) {
					setTheme(next);
				}
			}}
			variant="outline"
			size="sm"
			aria-label="Theme"
			className={className}
			data-test="color-mode-toggle"
		>
			{OPTIONS.map((option) => (
				<ToggleGroupItem
					key={option.value}
					value={option.value}
					aria-label={option.label}
					data-test={`color-mode-toggle-item-${option.value}`}
					className="px-2"
				>
					<option.icon />
				</ToggleGroupItem>
			))}
		</ToggleGroup>
	);
}
